import { useCallback, useEffect, useRef, useState } from 'react';
import { UseFormGetValues } from 'react-hook-form';
import { ArticleFormData } from '@/types/article';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UseArticleDraftAutoSaveProps {
  getValues: UseFormGetValues<ArticleFormData>;
  articleId?: string;
  enabled?: boolean;
  interval?: number;
  onDraftCreated?: (draftId: string) => void;
  selectedTags?: string[];
}

export const useArticleDraftAutoSave = ({
  getValues,
  articleId,
  enabled = true,
  interval = 30000,
  onDraftCreated,
  selectedTags = []
}: UseArticleDraftAutoSaveProps) => {
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState<string | undefined>(articleId);
  const lastSavedDataRef = useRef<string>('');
  
  // CRITICAL FIX: Use ref for synchronous save-in-progress check to prevent race conditions
  const savingRef = useRef(false);
  // Track draft ID synchronously to prevent duplicate creates
  const draftIdRef = useRef<string | undefined>(articleId);

  // Keep draftIdRef in sync
  useEffect(() => {
    draftIdRef.current = currentDraftId;
  }, [currentDraftId]);

  const saveDraftToDatabase = useCallback(async (): Promise<boolean> => {
    // CRITICAL: Use ref for synchronous check - state updates are async and can cause race conditions
    if (!enabled || savingRef.current) {
      console.log('⏳ Auto-save skipped:', { enabled, savingInProgress: savingRef.current });
      return false;
    }

    // Set lock immediately using ref (synchronous)
    savingRef.current = true;
    setIsSaving(true);

    try {
      const formData = getValues();
      
      // Don't save if title and content are both empty
      if (!formData.title?.trim() && !formData.content?.trim()) {
        return false;
      }

      const formDataWithTags = {
        ...formData,
        tag_ids: selectedTags
      };

      const dataToSave = JSON.stringify(formDataWithTags);
      
      // Only save if data has changed
      if (dataToSave === lastSavedDataRef.current) {
        return false;
      }

      // Generate unique slug for drafts
      const generateSlug = (title: string): string => {
        const baseSlug = title
          .toLowerCase()
          .trim()
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .slice(0, 80) || 'untitled-draft';
        
        // Add timestamp to ensure uniqueness
        return `${baseSlug}-draft-${Date.now()}`;
      };

      // Prepare article data
      const articleData: any = {
        title: formData.title || 'Untitled Draft',
        content: formData.content || '',
        excerpt: formData.excerpt || null,
        featured_image_url: formData.featured_image_url || null,
        featured_image_alt: formData.featured_image_alt || null,
        meta_description: formData.meta_description || null,
        focus_keyword: formData.focus_keyword || null,
        canonical_url: formData.canonical_url || null,
        og_title: formData.og_title || null,
        og_description: formData.og_description || null,
        og_image_url: formData.og_image_url || null,
        category_id: formData.category_id || null,
        author_id: formData.author_id || null,
        status: 'draft' as const,
        scheduled_at: null,
        timezone: formData.timezone || 'UTC',
      };

      let result;

      // Use ref for synchronous draft ID check
      const existingDraftId = draftIdRef.current;

      if (existingDraftId) {
        // Update existing draft
        console.log('📝 Auto-save: Updating existing draft:', existingDraftId);
        const { data, error } = await supabase
          .from('articles')
          .update(articleData)
          .eq('id', existingDraftId)
          .select()
          .single();

        if (error) {
          console.error('❌ Auto-save update error:', error);
          throw error;
        }
        result = data;
      } else {
        // Create new draft with unique slug
        const newSlug = generateSlug(formData.title || 'draft');
        console.log('✨ Auto-save: Creating new draft with slug:', newSlug);
        
        const { data, error } = await supabase
          .from('articles')
          .insert([{ ...articleData, slug: newSlug }])
          .select()
          .single();

        if (error) {
          console.error('❌ Auto-save insert error:', error);
          throw error;
        }
        result = data;
        
        // Update both ref (synchronous) and state (async)
        draftIdRef.current = result.id;
        setCurrentDraftId(result.id);
        
        if (onDraftCreated) {
          onDraftCreated(result.id);
        }
      }

      // Handle tags
      if (result.id && selectedTags && selectedTags.length > 0) {
        console.log('🏷️ Auto-save: Saving', selectedTags.length, 'tags for article', result.id);
        
        // First delete existing tags, then insert new ones
        await supabase
          .from('article_tags')
          .delete()
          .eq('article_id', result.id);
        
        const tagRelations = selectedTags.map(tagId => ({
          article_id: result.id,
          tag_id: tagId
        }));

        const { error: tagError } = await supabase
          .from('article_tags')
          .insert(tagRelations);
        
        if (tagError) {
          console.error('❌ Auto-save: Failed to save tags:', tagError);
          // Don't throw - tags are not critical
        } else {
          console.log('✅ Auto-save: Tags saved successfully');
        }
      } else if (result.id && (!selectedTags || selectedTags.length === 0)) {
        // Remove all tags if none selected
        await supabase
          .from('article_tags')
          .delete()
          .eq('article_id', result.id);
      }

      lastSavedDataRef.current = dataToSave;
      setLastSaveTime(new Date());
      console.log('✅ Auto-save: Draft saved successfully at', new Date().toISOString());
      
      return true;
    } catch (error) {
      console.error('❌ Failed to auto-save draft:', error);
      return false;
    } finally {
      // CRITICAL: Always release lock in finally block
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [getValues, enabled, onDraftCreated, selectedTags]);

  // Auto-save interval (configurable)
  useEffect(() => {
    if (!enabled || interval === 0) return; // Don't auto-save if disabled

    const intervalId = setInterval(() => {
      saveDraftToDatabase();
    }, interval);

    // Save on unmount
    return () => {
      clearInterval(intervalId);
      saveDraftToDatabase();
    };
  }, [saveDraftToDatabase, enabled, interval]);

  // Save before page unload
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = () => {
      // Use synchronous approach for beforeunload
      saveDraftToDatabase();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveDraftToDatabase, enabled]);

  const manualSave = useCallback(async () => {
    const success = await saveDraftToDatabase();
    if (success) {
      toast.success('Draft saved successfully');
    }
    return success;
  }, [saveDraftToDatabase]);

  return {
    saveDraft: manualSave,
    lastSaveTime,
    isSaving,
    currentDraftId
  };
};
